/**
 * Global sonner toast hardening.
 *
 * Feb 18, 2026 — Prod bug: when a FastAPI endpoint raises an HTTPException
 * with `detail={...}` (e.g. the intake-incomplete 409 returned by
 * `PATCH /candidates/{id}/stage`), call sites like
 * `toast.error(e?.response?.data?.detail || "fallback")` end up passing
 * an OBJECT to sonner. Sonner tries to render it as a React child →
 * React error #31 ("Objects are not valid as a React child") →
 * the ENTIRE React tree unmounts → user sees a blank white page.
 *
 * This module patches `toast.error/success/info/warning/message/loading`
 * so that any object/array/undefined argument is safely coerced into a
 * string (via `formatApiError`) BEFORE reaching sonner. That protects
 * against every existing `toast.error(err?.response?.data?.detail)` call
 * site in the codebase without a heavy refactor.
 *
 * Called once, at module import time, from `App.js`.
 */
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const STRING_KINDS = ["error", "success", "info", "warning", "message", "loading"];

function coerce(arg) {
    if (arg == null) return arg;
    if (typeof arg === "string" || typeof arg === "number") return arg;
    // React elements are objects with `$$typeof` — leave those alone.
    if (typeof arg === "object" && arg.$$typeof) return arg;
    // Explicit "content" shape: {title, description} → keep as-is.
    if (typeof arg === "object" && (typeof arg.title === "string" || typeof arg.description === "string")) {
        return arg;
    }
    const s = formatApiError(arg);
    return s || "Something went wrong. Please try again.";
}

let patched = false;
export function hardenSonner() {
    if (patched) return;
    patched = true;
    for (const kind of STRING_KINDS) {
        const orig = toast[kind];
        if (typeof orig !== "function") continue;
        toast[kind] = (msg, opts) => orig(coerce(msg), opts);
    }
}
